import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { XIcon, MicIcon } from 'lucide-react';
import { ChatInput } from './ChatInput';
import { ChatMessage } from './ChatMessage';
import { Logo } from './common/Logo';
import { mockData } from '../utils/mockData';
import type { Message } from '../App';
interface ChatViewProps {
  messages: Message[];
  isLoading: boolean;
  onSendMessage: (message: string) => void;
  isVoiceMode?: boolean;
  onToggleVoiceMode?: () => void;
}
export const ChatView = ({
  messages,
  isLoading,
  onSendMessage,
  isVoiceMode,
  onToggleVoiceMode
}: ChatViewProps) => {
  const {
    id
  } = useParams();
  const [transcribedText, setTranscribedText] = useState('');
  useEffect(() => {
    if (isVoiceMode) {
      setTranscribedText('');
      const sampleText = "Searching for recent incidents in the Willow Street area... I've found three related reports from the last 24 hours.";
      const words = sampleText.split(' ');
      let currentWordIndex = 0;
      const interval = setInterval(() => {
        if (currentWordIndex < words.length) {
          setTranscribedText(prev => prev + (prev ? ' ' : '') + words[currentWordIndex]);
          currentWordIndex++;
        } else {
          clearInterval(interval);
        }
      }, 150);
      return () => clearInterval(interval);
    }
  }, [isVoiceMode]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Get the correct conversation based on the chat ID
  const conversationMessages = id ? mockData.conversations[id as keyof typeof mockData.conversations] || messages : messages;
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [conversationMessages]);
  return <div className="flex flex-col h-full min-h-0 w-full bg-[#0a1120] relative" data-id="element-80">
      {isVoiceMode && <div className="absolute inset-0 z-50 bg-command-app/98 backdrop-blur-2xl flex flex-col items-center justify-center p-8 animate-fade-in" data-id="element-81">
          <button onClick={onToggleVoiceMode} className="absolute top-8 right-8 p-4 hover:bg-white/5 rounded-full text-command-text-muted hover:text-white transition-colors z-50" data-id="element-82">
            <XIcon size={32} className="text-[#4a90e2]" data-id="element-83" />
          </button>

          <div className="relative mb-16" data-id="element-84">
            <div className="w-64 h-64 p-[2px] rounded-full running-border relative z-10 flex items-center justify-center" data-id="element-85">
              <div className="w-full h-full rounded-full bg-command-sidebar relative z-10 flex items-center justify-center" data-id="element-86">
                <Logo size={120} className="text-[#4a90e2]" data-id="element-87" />
              </div>
            </div>
          </div>

          <div className="text-center max-w-2xl animate-slide-up" data-id="element-88">
            <div className="flex items-center justify-center gap-2 mb-4" data-id="element-89">
              <div className="w-2 h-2 rounded-full bg-[#f59e0b] animate-pulse" data-id="element-90" />
              <span className="text-xs font-bold text-[#f59e0b] uppercase tracking-[0.3em]" data-id="element-91">
                Listening
              </span>
            </div>
            <p className="text-3xl md:text-4xl font-medium text-white leading-tight opacity-90" data-id="element-92">
              {transcribedText}
            </p>
          </div>
        </div>}
      <div className="flex-grow overflow-y-auto min-h-0 p-8 w-full" data-id="element-93">
        <div className="max-w-[1000px] mx-auto w-full py-8" data-id="element-94">
          {conversationMessages.map(message => <ChatMessage key={message.id} message={message} data-id="element-95" />)}
          {isLoading && <div className="flex items-center gap-2 p-4 ml-16" data-id="element-96">
              <div className="flex space-x-2" data-id="element-97">
                {[0, 200, 400].map(delay => <div key={delay} className="w-2.5 h-2.5 bg-[#4a90e2] rounded-full animate-bounce" style={{
              animationDelay: `${delay}ms`,
              animationDuration: '600ms'
            }} data-id="element-98" />)}
              </div>
            </div>}
          <div ref={messagesEndRef} data-id="element-99" />
        </div>
      </div>

      <div className="flex-shrink-0 p-8 pt-4 border-t border-white/5 bg-[#0a1120]/80 backdrop-blur-md w-full" data-id="element-100">
        <ChatInput onSendMessage={onSendMessage} isVoiceMode={isVoiceMode} onToggleVoiceMode={onToggleVoiceMode} data-id="element-101" />
      </div>
    </div>;
};